import {
  Flex,
  Heading,
  SelectMultiple,
  Text,
  Skeleton,
  SegmentedControl,
} from '@scale/scaleui-radix';
import { AuditRubricCriterion } from '@scale/shared-types';

export const FillCriterionErrorCategories = ({
  errorCategories,
  selectedErrorCategories,
  setSelectedErrorCategories,
}: {
  errorCategories: string[];
  selectedErrorCategories: string[];
  setSelectedErrorCategories: (errorCategories: string[]) => void;
}) => {
  return (
    <Flex direction="column" gap="2">
      <Text weight="medium">Error Categories</Text>
      {errorCategories.length > 0 ? (
        <SelectMultiple.Root
          value={selectedErrorCategories}
          onValueChange={setSelectedErrorCategories}
        >
          <SelectMultiple.Trigger className="w-full" />
          <SelectMultiple.Content>
            {errorCategories.map(errorCategory => (
              <SelectMultiple.Item key={errorCategory} value={errorCategory}>
                {errorCategory}
              </SelectMultiple.Item>
            ))}
          </SelectMultiple.Content>
        </SelectMultiple.Root>
      ) : (
        <Text size="1" color="neutral">
          You have no error categories set for this criterion.
        </Text>
      )}
    </Flex>
  );
};

const ViewCriterionErrorCategories = ({ criterion }: { criterion: AuditRubricCriterion }) => {
  return (
    <Flex direction="column" gap="1" width="100%">
      <Text color="neutral">Error Categories</Text>
      <Text>
        {criterion.useAllErrorCategories ? 'All' : criterion.errorCategories.length} categories
      </Text>
    </Flex>
  );
};

const EditCriterionErrorCategories = ({
  criterion,
  setCriterion,
  isEditingCriterion,
  allErrorCategories,
  isLoadingErrorCategories,
  errorCategoriesError,
  error,
}: {
  criterion: AuditRubricCriterion;
  setCriterion: (criterion: AuditRubricCriterion) => void;
  isEditingCriterion: boolean;
  allErrorCategories?: string[];
  isLoadingErrorCategories: boolean;
  errorCategoriesError?: string;
  error?: string;
}) => {
  const setUseAllErrorCategories = (useAllErrorCategories: boolean) => {
    setCriterion({ ...criterion, useAllErrorCategories });
  };

  const setErrorCategories = (errorCategories: string[]) => {
    setCriterion({ ...criterion, errorCategories });
  };

  const editingErrorCategoriesSelected = (
    <>
      <Text size="1" color="neutral">
        Selected categories are applied to the task if the score falls within their configured
        range.
      </Text>
      {errorCategoriesError ? (
        <Text size="1" color="neutral">
          {errorCategoriesError ?? 'Error loading error categories'}
        </Text>
      ) : isLoadingErrorCategories ? (
        <Skeleton></Skeleton>
      ) : allErrorCategories && allErrorCategories.length > 0 ? (
        <Flex direction="row" gap="2" width="100%">
          <SelectMultiple.Root
            defaultValue={criterion.errorCategories}
            onValueChange={setErrorCategories}
          >
            <SelectMultiple.Trigger className="w-full" />
            <SelectMultiple.Content>
              {allErrorCategories.map(errorCategory => (
                <SelectMultiple.Item key={errorCategory} value={errorCategory}>
                  {errorCategory}
                </SelectMultiple.Item>
              ))}
            </SelectMultiple.Content>
          </SelectMultiple.Root>
        </Flex>
      ) : (
        <Text size="1" color="neutral">
          You have no error categories set for this project.
        </Text>
      )}
    </>
  );

  const editingErrorCategoriesAll = (
    <>
      <Text size="1" color="neutral">
        All project-configured error categories are applied to the task if the score falls within
        their configured range.
      </Text>
    </>
  );

  return (
    <Flex
      direction={isEditingCriterion ? 'column' : 'row'}
      gap="2"
      align={isEditingCriterion ? 'start' : 'center'}
      width="100%"
    >
      <Heading size="2">Error Categories</Heading>
      <SegmentedControl.Root
        value={
          criterion.useAllErrorCategories ? 'all-error-categories' : 'selected-error-categories'
        }
        style={{ width: '100%' }}
      >
        <SegmentedControl.Item
          value="all-error-categories"
          onClick={() => setUseAllErrorCategories(true)}
        >
          All categories
        </SegmentedControl.Item>
        <SegmentedControl.Item
          value="selected-error-categories"
          onClick={() => setUseAllErrorCategories(false)}
        >
          Selected categories
        </SegmentedControl.Item>
      </SegmentedControl.Root>
      {criterion.useAllErrorCategories ? editingErrorCategoriesAll : editingErrorCategoriesSelected}
      {error && (
        <Text size="2" color="red">
          {error}
        </Text>
      )}
    </Flex>
  );
};

export const CriterionErrorCategories = ({
  criterion,
  setCriterion,
  isEditingCriterion,
  allErrorCategories,
  isLoadingErrorCategories,
  errorCategoriesError,
  error,
}: {
  criterion: AuditRubricCriterion;
  setCriterion: (criterion: AuditRubricCriterion) => void;
  isEditingCriterion: boolean;
  allErrorCategories?: string[];
  isLoadingErrorCategories: boolean;
  errorCategoriesError?: string;
  error?: string;
}) => {
  return isEditingCriterion ? (
    <EditCriterionErrorCategories
      criterion={criterion}
      setCriterion={setCriterion}
      isEditingCriterion={isEditingCriterion}
      allErrorCategories={allErrorCategories}
      isLoadingErrorCategories={isLoadingErrorCategories}
      errorCategoriesError={errorCategoriesError}
      error={error}
    />
  ) : (
    <ViewCriterionErrorCategories criterion={criterion} />
  );
};
